/**
 * TryPage — Public page for instant post generation without sign-up
 * Accessible at /try without authentication
 */

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';

interface GeneratedPost {
  body: string;
  title?: string | null;
  hookVariants?: string[];
  ctaVariants?: string[];
}

export function TryPage() {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [post, setPost] = useState<GeneratedPost | null>(null);
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleGenerate = async () => {
    if (!text.trim() || generating) return;

    setGenerating(true);
    setError(null);
    setPost(null);

    try {
      const res = await fetch(`${API_BASE}/api/public/generate-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim() }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error?.message || 'Something went wrong. Please try again.');
        setGenerating(false);
        return;
      }

      setPost(data.data);
    } catch {
      setError('Network error. Please check your connection and try again.');
    }

    setGenerating(false);
  };

  const handleCopy = async () => {
    if (!post?.body) return;
    try {
      await navigator.clipboard.writeText(post.body);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = post.body;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const charCount = text.length;
  const isReady = charCount >= 50;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fafafa', fontFamily: "'Outfit', 'Inter', system-ui, sans-serif" }}>
      {/* Header */}
      <header style={{ padding: '1.5rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '64rem', margin: '0 auto' }}>
        <div
          onClick={() => navigate('/')}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
        >
          <img src="/logo.svg" alt="Mazo" style={{ width: '1.75rem', height: '1.75rem' }} />
          <span style={{ fontWeight: 600, fontSize: '1.25rem', color: '#111' }}>Mazo</span>
        </div>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '0.5rem 1.25rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#fff',
            backgroundColor: '#6d28d9',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
          }}
        >
          Sign In
        </button>
      </header>

      {/* Main content */}
      <main style={{ maxWidth: '48rem', margin: '0 auto', padding: '2rem 1.5rem 4rem' }}>
        {/* Headline */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <h1 style={{ fontSize: '2.25rem', fontWeight: 700, color: '#111', lineHeight: 1.2, marginBottom: '0.75rem' }}>
            Turn any conversation into a LinkedIn post
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#6b7280', maxWidth: '36rem', margin: '0 auto' }}>
            Paste your meeting transcript, notes, or any text below. Mazo will extract the key insights and craft a ready-to-publish LinkedIn post.
          </p>
        </div>

        {/* Input section */}
        {!post && (
          <div style={{ backgroundColor: '#fff', borderRadius: '0.75rem', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '1.5rem' }}>
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste your meeting transcript or notes here..."
              disabled={generating}
              style={{
                width: '100%',
                minHeight: '14rem',
                padding: '1rem',
                fontSize: '0.9375rem',
                lineHeight: 1.6,
                color: '#111',
                border: '1px solid #d1d5db',
                borderRadius: '0.5rem',
                resize: 'vertical',
                outline: 'none',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                backgroundColor: generating ? '#f9fafb' : '#fff',
              }}
              onFocus={(e) => { e.target.style.borderColor = '#8b5cf6'; e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.75rem' }}>
              <span style={{ fontSize: '0.8125rem', color: isReady ? '#6b7280' : '#ef4444' }}>
                {charCount < 50 ? `${50 - charCount} more characters needed` : `${charCount.toLocaleString()} characters`}
              </span>
              <button
                onClick={handleGenerate}
                disabled={!isReady || generating}
                style={{
                  padding: '0.625rem 1.5rem',
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: !isReady || generating ? '#c4b5fd' : '#7c3aed',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: !isReady || generating ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'background-color 0.15s',
                  fontFamily: 'inherit',
                }}
              >
                {generating && (
                  <span style={{
                    display: 'inline-block',
                    width: '1rem',
                    height: '1rem',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                    animation: 'spin 0.6s linear infinite',
                  }} />
                )}
                {generating ? 'Generating...' : 'Generate Post'}
              </button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem 1rem',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '0.5rem',
            color: '#dc2626',
            fontSize: '0.875rem',
          }}>
            {error}
          </div>
        )}

        {/* Generated post — LinkedIn preview card */}
        {post && (
          <div style={{ marginTop: '0rem' }}>
            {/* LinkedIn-style preview card */}
            <div style={{
              backgroundColor: '#fff',
              borderRadius: '0.75rem',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              overflow: 'hidden',
            }}>
              {/* Card header */}
              <div style={{ padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{
                  width: '2.5rem',
                  height: '2.5rem',
                  borderRadius: '50%',
                  backgroundColor: '#7c3aed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '1rem',
                }}>
                  Y
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9375rem', color: '#111' }}>You</div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>LinkedIn post preview</div>
                </div>
              </div>

              {/* Post body */}
              <div style={{ padding: '1.25rem', whiteSpace: 'pre-wrap', fontSize: '0.9375rem', lineHeight: 1.7, color: '#111' }}>
                {post.body}
              </div>

              {/* Actions bar */}
              <div style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <button
                  onClick={handleCopy}
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: copied ? '#059669' : '#374151',
                    backgroundColor: copied ? '#ecfdf5' : '#f9fafb',
                    border: '1px solid ' + (copied ? '#a7f3d0' : '#e5e7eb'),
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    transition: 'all 0.15s',
                  }}
                >
                  {copied ? 'Copied!' : 'Copy to clipboard'}
                </button>
                <button
                  onClick={() => { setPost(null); setError(null); }}
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: '#374151',
                    backgroundColor: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Try again
                </button>
              </div>
            </div>

            {/* CTA */}
            <div style={{
              marginTop: '1.5rem',
              textAlign: 'center',
              padding: '2rem',
              backgroundColor: '#f5f3ff',
              borderRadius: '0.75rem',
              border: '1px solid #ede9fe',
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111', marginBottom: '0.5rem' }}>
                Like what you see?
              </h3>
              <p style={{ fontSize: '0.9375rem', color: '#6b7280', marginBottom: '1.25rem' }}>
                Sign up to save your posts, train your voice profile, connect sources, and publish directly to LinkedIn.
              </p>
              <button
                onClick={() => navigate('/')}
                style={{
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: '#7c3aed',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Sign up to save &amp; publish
              </button>
            </div>
          </div>
        )}

        {/* How it works — shown below input */}
        {!post && (
          <div style={{ marginTop: '3rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', textAlign: 'center', marginBottom: '1.5rem' }}>
              How it works
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
              {[
                { step: '1', title: 'Paste your text', desc: 'Meeting transcript, notes, brainstorm, or any raw content.' },
                { step: '2', title: 'AI extracts insights', desc: 'Key takeaways, stories, and frameworks are identified.' },
                { step: '3', title: 'Get your post', desc: 'A polished LinkedIn post, ready to copy and publish.' },
              ].map((item) => (
                <div key={item.step} style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '2rem',
                    height: '2rem',
                    borderRadius: '50%',
                    backgroundColor: '#ede9fe',
                    color: '#7c3aed',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 0.75rem',
                  }}>
                    {item.step}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: '0.9375rem', color: '#111', marginBottom: '0.25rem' }}>{item.title}</div>
                  <div style={{ fontSize: '0.8125rem', color: '#6b7280', lineHeight: 1.5 }}>{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Spinner animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
